import { TextField, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { NewMoneyFormatv2 } from '../../../library/MoneyFormat';
import { showWarningSnackbar } from '../../../components/snackBar/snakbar';

const CREDIT_LIMIT_MAX = 9999999999.99;
const CREDIT_LIMIT_MAX_MESSAGE = 'Credit Limit cannot exceed 9,99,99,99,999.99';

/** Count digits in `str` before position `pos`. */
function digitCountBefore(str, pos) {
  let count = 0;
  for (let i = 0; i < Math.min(pos, (str || '').length); i++) {
    if (/\d/.test(str[i])) count++;
  }
  return count;
}

/** Position in formatted string after the n-th digit (for cursor placement). */
function positionAfterDigits(formatted, n) {
  let count = 0;
  for (let i = 0; i < (formatted || '').length; i++) {
    if (/\d/.test(formatted[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return (formatted || '').length;
}

/** Map raw cursor index to formatted cursor index (handles commas + decimals). */
function positionFromRawIndex(formatted, raw, rawIndex) {
  const f = formatted || '';
  const r = raw || '';
  let i = 0;
  let j = 0;

  while (i < Math.max(0, rawIndex) && j < f.length) {
    if (r[i] === f[j]) {
      i++;
      j++;
    } else {
      j++;
    }
  }
  return j;
}

const FormikMoneyInput = memo(
  React.forwardRef(({ formik, name, label, viewPage, setGstApi }, ref) => {
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    const nextCursorRef = useRef(null);

    useEffect(() => {
      const el = inputRef.current;
      if (el && nextCursorRef.current != null) {
        const pos = nextCursorRef.current;
        el.setSelectionRange(pos, pos);
        nextCursorRef.current = null;
      }
    }, [value]);

    const handleChange = (event) => {
      const rawInput = (event.target.value || '').replace(/,/g, '');
      const selectionStart = event.target.selectionStart ?? 0;
      const rawCursorPos = (event.target.value || '').slice(0, selectionStart).replace(/,/g, '').length;

      if (name === 'creditPeriod') {
        const credit = rawInput;
        if ((credit > 0 && credit < 1200) || credit === '') {
          if (/^\d+$/.test(credit)) {
            setValue(credit);
          } else {
            formik.setFieldValue(name, '');
            setValue('');
          }
        } else if (event.target.value >= 1200) {
          showWarningSnackbar('Credit period must be less than 1200 days');
        }
      } else if (name !== 'creditPeriod') {
        const credit = rawInput;
        const isNumberLike = /^\d*\.?\d*$/.test(credit);
        const hasValidPrecision = /^\d+(\.\d{0,2})?$/.test(credit);
        const numericValue = Number(credit);
        if (credit.length === 0) {
          formik.setFieldValue(name, '');
          setValue('');
        } else if (!isNumberLike || Number.isNaN(numericValue)) {
          showWarningSnackbar('Credit Limit must be a valid number');
        } else if (!hasValidPrecision) {
          showWarningSnackbar('Credit Limit supports up to 2 decimal places');
        } else if (numericValue > CREDIT_LIMIT_MAX) {
          showWarningSnackbar(CREDIT_LIMIT_MAX_MESSAGE);
        } else {
          setValue(credit);
          const formatted = NewMoneyFormatv2(credit);
          nextCursorRef.current = positionFromRawIndex(formatted, credit, rawCursorPos);
        }
      }
    };

    return (
      <>
        <TextField
          inputRef={inputRef}
          label={label}
          disabled={viewPage}
          inputProps={{ maxLength: 30 }}
          value={(function () {
            const rawSource = value !== '' ? value?.toString() : formik?.values[name]?.toString();
            const cleaned = (rawSource || '').replace(/,/g, '');
            if (cleaned === '' || cleaned == 0) return '';
            if (name === 'creditPeriod') {
              return cleaned;
            }
            return NewMoneyFormatv2(cleaned);
          })()}
          onChange={handleChange}
          onFocus={() => {
            setValue(formik.values[name]?.toString());
            formik.setFieldTouched([name], true);
          }}
          sx={{
            '.MuiFormLabel-asterisk': {
              color: 'danger.main',
              fontSize: '20px',
              fontWeight: 'bold',
            },
          }}
          fullWidth
          onBlur={() => {
            const raw = (value ?? formik.values[name] ?? '').toString();
            const cleaned = raw.replace(/,/g, '');
            setValue('');
            formik.setFieldValue(name, cleaned);
          }}
          size="small"
          name={name}
        />
        {formik.touched?.[name] && formik.errors?.[name] ? (
          <Typography variant="body1" className="error" style={{ fontSize: 12, padding: 0 }}>
            {formik.errors[name]}
          </Typography>
        ) : (
          <p style={{ padding: 9 }} />
        )}
      </>
    );
  })
);
export default FormikMoneyInput;
